// Libs
import classNames from "classnames";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Link } from "react-router-dom";
import classnames from "classnames";

// DLS
import "@collectivehealth/dls-icons/dist/es/elements/dls-icon-close.js";
import "@collectivehealth/dls-logos/dist/es/elements/dls-logo-brandmark.js";
import "@collectivehealth/dls-tooltips";

// Components
import RenderIf from "components/RenderIf";
import SubMenu from "components/sidebar/SubMenu";
import IconButton from "components/buttons/IconButton";
import MenuLink from "components/sidebar/MenuLink";

// Styles
import styles from "./Sidebar.css";

// Utils
import { hasSubMenuItems } from "utils/routing/routing";

// Constants
export const DATA_HOOKS = { ROOT: "Sidebar", MENU: "SidebarMenu" };

export default class Sidebar extends Component {
  constructor(props) {
    super(props);
    this.defaultFocus = React.createRef();
  }

  // Compound components
  static SubMenu = SubMenu;

  static defaultProps = {
    isDisplayed: false
  };

  static propTypes = {
    activeMenuItem: PropTypes.object.isRequired,
    menuItems: PropTypes.arrayOf(PropTypes.object),
    isDisplayed: PropTypes.bool.isRequired,
    onClickCloseButton: PropTypes.func.isRequired
  };

  state = {
    isSubMenuDisplayed: false
  };

  componentDidMount() {
    this.setState({
      isSubMenuDisplayed: hasSubMenuItems(this.props.activeMenuItem)
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.menuItems !== this.props.menuItems) {
      this.setState({
        isSubMenuDisplayed: hasSubMenuItems(this.props.activeMenuItem)
      });
    }
  }

  render() {
    const { activeMenuItem, menuItems, isDisplayed, onClickCloseButton } = this.props;
    const { isSubMenuDisplayed } = this.state;

    return (
      <>
        <div
          className={classNames(styles.root, { [styles.hidden]: !isDisplayed })}
          data-hook={DATA_HOOKS.ROOT}
        >
          <IconButton className={styles.closeButton} onClick={onClickCloseButton}>
            <dls-icon-close />
          </IconButton>

          <div className={styles.menuWrapper}>
            <div className={styles.menuContainer}>
              <div
                className={classnames(styles.menu, { [styles.hidden]: !isDisplayed })}
                data-hook={DATA_HOOKS.MENU}
              >
                <Link to="/" className={styles.menuLogo} tabIndex="-1" ref={this.defaultFocus}>
                  <dls-logo-brandmark />
                </Link>
                {this.renderMenuLinks(menuItems, isSubMenuDisplayed)}
              </div>
              <Sidebar.SubMenu
                closeSubMenu={this.closeSubMenu}
                isDisplayed={isSubMenuDisplayed}
                subMenuItems={activeMenuItem.subMenuItems}
                title={activeMenuItem.title}
                subtitle={activeMenuItem.subtitle}
                defaultFocus={this.defaultFocus}
              />
            </div>
          </div>
        </div>

        {/* Navigation Overlay */}
        <div
          className={classNames(styles.menuOverlay, { [styles.hidden]: !isDisplayed })}
          onClick={onClickCloseButton}
        />
      </>
    );
  }

  // EVENT HANDLERS

  /**
   * Closes the SubMenu.
   */
  closeSubMenu = () => {
    this.setState({ isSubMenuDisplayed: false });
    this.defaultFocus.current.focus();
  };

  /**
   * Opens the SubMenu.
   */
  openSubMenu = () => {
    this.setState({ isSubMenuDisplayed: true });
  };

  // RENDERERS

  /**
   * Renders a list of menu links.
   * @param {Object[]} menuItems List of menu items to render
   * @param {boolean} isSubMenuDisplayed Submenu in display indicator
   * @returns {Component[]} List of menu link components
   */
  renderMenuLinks = (menuItems, isSubMenuDisplayed) => {
    return menuItems.map(menuItem => {
      if (!menuItem) {
        return null;
      }

      // TODO: combine with LaunchDarkly context to determine the value of `hasNewLabel`
      const {
        hasDivider = false,
        path,
        search,
        title,
        subtitle,
        supportLabel,
        hasExternalLinkIcon
      } = menuItem;
      const hasSubMenu = hasSubMenuItems(menuItem);

      // Must use default attributes 'align' and 'position' of dls-tooltip in order to
      // make it works with custom 'sidebar-theme'.
      const menuLink = (
        <dls-tooltip
          theme="sidebar-theme"
          disabled={isSubMenuDisplayed ? "" : "disabled"}
          indicator="noIndicator"
          delay="0"
        >
          <div slot="tooltip-trigger">
            <MenuLink
              hasSubMenu={hasSubMenu}
              isDisplayed={!isSubMenuDisplayed}
              onClick={hasSubMenu ? this.openSubMenu : this.closeSubMenu}
              path={path}
              search={search}
              title={title}
              subtitle={subtitle}
              hasNewLabel={supportLabel}
              hasExternalLinkIcon={hasExternalLinkIcon}
              hideTitle
            />
          </div>
          <div slot="tooltip-window-body">{subtitle ? `${subtitle}` : `${title}`}</div>
        </dls-tooltip>
      );

      return (
        <React.Fragment key={title}>
          <RenderIf test={hasDivider}>
            <div className={styles.menuItemDivider} />
          </RenderIf>
          {menuLink}
        </React.Fragment>
      );
    });
  };
}
